import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { products } from '@/mocks/products';
import QuickView from './QuickView';
import BagDrawer from './BagDrawer';

export type Product = (typeof products)[number];
export type BagLine = { id: string; qty: number };

type BagCtx = {
  lines: BagLine[];
  count: number;
  subtotal: number;
  add: (id: string, qty: number) => void;
  setQty: (id: string, qty: number) => void;
  remove: (id: string) => void;
  openBag: () => void;
  openProduct: (id: string) => void;
};

const Ctx = createContext<BagCtx | null>(null);
const KEY = 'ff-bag';

export const findProduct = (id: string) => products.find((p) => p.id === id);

function loadLines(): BagLine[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as BagLine[]) : [];
    return parsed.filter((l) => findProduct(l.id) && l.qty > 0);
  } catch {
    return [];
  }
}

export function BagProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<BagLine[]>(loadLines);
  const [bagOpen, setBagOpen] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(lines));
  }, [lines]);

  const add = useCallback((id: string, qty: number) => {
    setLines((prev) => {
      const hit = prev.find((l) => l.id === id);
      if (hit) return prev.map((l) => (l.id === id ? { ...l, qty: Math.min(20, l.qty + qty) } : l));
      return [...prev, { id, qty }];
    });
  }, []);
  const setQty = useCallback((id: string, qty: number) => {
    setLines((prev) => (qty <= 0 ? prev.filter((l) => l.id !== id) : prev.map((l) => (l.id === id ? { ...l, qty: Math.min(20, qty) } : l))));
  }, []);
  const remove = useCallback((id: string) => setLines((prev) => prev.filter((l) => l.id !== id)), []);

  const value = useMemo<BagCtx>(() => ({
    lines,
    count: lines.reduce((s, l) => s + l.qty, 0),
    subtotal: lines.reduce((s, l) => s + (findProduct(l.id)?.price ?? 0) * l.qty, 0),
    add,
    setQty,
    remove,
    openBag: () => setBagOpen(true),
    openProduct: (id: string) => setProductId(id),
  }), [lines, add, setQty, remove]);

  const product = productId ? findProduct(productId) : undefined;

  return (
    <Ctx.Provider value={value}>
      {children}
      {product && (
        <QuickView
          product={product}
          onClose={() => setProductId(null)}
          onAdd={(qty) => {
            add(product.id, qty);
            setProductId(null);
            setBagOpen(true);
          }}
        />
      )}
      {bagOpen && <BagDrawer onClose={() => setBagOpen(false)} />}
    </Ctx.Provider>
  );
}

export function useBag() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useBag must be used inside BagProvider');
  return c;
}